/**
 * SelectedPropertyContext — global single source of truth for the active property.
 * Replaces scattered recentProperties/localStorage state in Sidebar.tsx.
 * Bridges to usePropertyStore for backward compatibility.
 * T5
 */
import React, { createContext, useCallback, useState, useEffect } from 'react';
import { usePropertyStore } from '@/stores/property';

const STORAGE_KEY_SELECTED = 'fm_ui_selected_property';
const STORAGE_KEY_RECENT = 'fm_ui_recent_properties';
const LEGACY_KEY_RECENT = 'recentProperties'; // backward-compat: test + old code reads this key

export interface SelectedPropertySummary {
  id: string;
  name?: string;
  municipality?: string;
  area?: number;
  coordinates?: { lat: number; lng: number };
  lastAccessed: string;
}

interface SelectedPropertyContextType {
  selectedProperty: SelectedPropertySummary | null;
  recentProperties: SelectedPropertySummary[];
  setSelectedProperty: (p: SelectedPropertySummary) => void;
  clearSelectedProperty: () => void;
}

// eslint-disable-next-line react-refresh/only-export-components -- context object export required by sibling useSelectedProperty.ts hook
export const SelectedPropertyContext = createContext<SelectedPropertyContextType | undefined>(undefined);

function readStorage<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function writeStorage<T>(key: string, value: T): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // storage full — silent
  }
}

function dedupeById(list: SelectedPropertySummary[]): SelectedPropertySummary[] {
  const seen = new Set<string>();
  return list.filter((p) => {
    if (!p.id || seen.has(p.id)) return false;
    seen.add(p.id);
    return true;
  });
}

export const SelectedPropertyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [selectedProperty, setSelectedPropertyState] = useState<SelectedPropertySummary | null>(
    () => readStorage<SelectedPropertySummary | null>(STORAGE_KEY_SELECTED, null),
  );
  const [recentProperties, setRecentPropertiesState] = useState<SelectedPropertySummary[]>(
    () => {
      // Migrate from legacy Sidebar key on first load
      const legacy = readStorage<SelectedPropertySummary[]>('recentProperties', []);
      const modern = readStorage<SelectedPropertySummary[]>(STORAGE_KEY_RECENT, []);
      const merged = dedupeById([...modern, ...legacy]).slice(0, 5);
      if (merged.length > 0) {
        writeStorage(STORAGE_KEY_RECENT, merged);
        writeStorage(LEGACY_KEY_RECENT, merged);
      }
      return merged;
    },
  );

  // One-way sync: usePropertyStore → SelectedPropertyContext
  const storeSelectedProperty = usePropertyStore((s) => s.selectedProperty);
  const selectedPropertyId = selectedProperty?.id;
  useEffect(() => {
    if (!storeSelectedProperty) return;
    const incoming = storeSelectedProperty.property_id;
    if (incoming === selectedPropertyId) return; // no change
    const summary: SelectedPropertySummary = {
      id: incoming,
      name: storeSelectedProperty.address,
      municipality: storeSelectedProperty.municipality,
      area: storeSelectedProperty.area_hectares,
      coordinates: storeSelectedProperty.coordinates,
      lastAccessed: new Date().toISOString(),
    };
    setSelectedPropertyState(summary);
    writeStorage(STORAGE_KEY_SELECTED, summary);
    setRecentPropertiesState((prev) => {
      const filtered = prev.filter((p) => p.id !== summary.id);
      const updated = dedupeById([summary, ...filtered]).slice(0, 5);
      writeStorage(STORAGE_KEY_RECENT, updated);
      writeStorage(LEGACY_KEY_RECENT, updated);
      return updated;
    });
  }, [storeSelectedProperty, selectedPropertyId]);

  const setSelectedProperty = useCallback((p: SelectedPropertySummary) => {
    const updated: SelectedPropertySummary = { ...p, lastAccessed: new Date().toISOString() };
    setSelectedPropertyState(updated);
    writeStorage(STORAGE_KEY_SELECTED, updated);
    setRecentPropertiesState((prev) => {
      const filtered = prev.filter((r) => r.id !== updated.id);
      const newList = dedupeById([updated, ...filtered]).slice(0, 5);
      writeStorage(STORAGE_KEY_RECENT, newList);
      writeStorage(LEGACY_KEY_RECENT, newList);
      return newList;
    });
  }, []);

  const clearSelectedProperty = useCallback(() => {
    setSelectedPropertyState(null);
    localStorage.removeItem(STORAGE_KEY_SELECTED);
  }, []);

  return (
    <SelectedPropertyContext.Provider
      value={{ selectedProperty, recentProperties, setSelectedProperty, clearSelectedProperty }}
    >
      {children}
    </SelectedPropertyContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components -- hook re-export; definition lives in useSelectedProperty.ts (non-component file)
export { useSelectedProperty } from '@/contexts/useSelectedProperty';
